//*Libraries imports
import { FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronDown } from "lucide-react";

//*Hooks Imports
import { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useCreateCreditCardPayment } from "@/hooks/asaas/payments/mutations/useCreateCreditCardPayment";

//*Components Imports
import { PaymentInnerHeader } from "./components/shared/PaymentInnerHeader";
import { SalesContractArea } from "./components/shared/SalesContractArea";
import { AwaitingApprovalModal } from "./components/credit-card/AwaitingApprovalModal";
import { PaymentErrorModal } from "./components/credit-card/PaymentErrorModal";
import { PaymentSuccessModal } from "./components/shared/PaymentSuccessModal";
import { ProductInfo } from "./components/shared/ProductInfo";
import { BillingAddressSection } from "./components/credit-card/BillingAddressSection";
import { CreditCardDataSection } from "./components/credit-card/CreditCardDataSection";
import { SavedCardsList } from "./components/credit-card/SavedCardsList";

//* Types
import { ISaleOutputDTO } from "@/schemas/sales";
import { ClientCreditCard, Client } from "@/schemas/client";

//* Utils
import { parseMongoData } from "@/utils/mongoUtils";
import { getSaleFinancialConfig } from "@helpers/saleHelper";
import { calculatePaymentValues } from "@helpers/paymentCalculator";
import { MethodPaymentEnum } from "@/schemas/methodpayment";
import {
  CreditCardFormSchema,
  CreditCardFormDTO,
  CreateCreditCardPaymentAsaasDTO,
} from "@/schemas/asaas/payment";
import { isSalePaid } from "./components/shared/utils";

export function PaymentCreditCard() {
  const navigate = useNavigate();
  const { sale } = useOutletContext<{ sale: ISaleOutputDTO | null }>();

  const getProductName = (product: any) => {
    if (!product) return "Produto não encontrado";
    if (typeof product === "object") return product.name;
    const match = product.match(/name:\s*'([^']+)'/);
    return match ? match[1] : product;
  };

  const mutateCreatePayment = useCreateCreditCardPayment();

  const clientData = parseMongoData<Client>(sale?.clientId);
  const clientPlace = clientData?.places?.[0];
  const savedCards: ClientCreditCard[] = clientData?.creditCards || [];

  const financialConfig = getSaleFinancialConfig(sale);
  const maxInstallments = financialConfig?.maxInstallments || 12;

  const installmentsOptions = sale
    ? Array.from({ length: maxInstallments }, (_, i) => {
        const count = i + 1;
        const calc = calculatePaymentValues({
          sale,
          billingType: MethodPaymentEnum.CREDIT_CARD,
          installmentCount: count,
        });

        const installmentValue = calc.finalValue / count;

        const value = installmentValue.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        const totalValue = calc.finalValue.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        });

        let label = `${count}x de R$ ${value}`;
        if (calc.interestApplied) {
          label += ` (Total: ${totalValue})`;
        } else {
          label += ` (Sem juros)`;
        }

        return {
          label,
          value: `${count}`,
        };
      })
    : [];

  const methods = useForm<CreditCardFormDTO>({
    resolver: zodResolver(CreditCardFormSchema),
    defaultValues: {
      creditCard: {
        number: "",
        holderName: clientData?.name || "",
        expiryMonth: "",
        expiryYear: "",
        ccv: "",
      },
      creditCardHolderInfo: {
        name: clientData?.name || "",
        email: clientData?.email || "",
        cpfCnpj: clientData?.cpf || clientData?.cnpj || "",
        postalCode: clientPlace?.zip_code || "",
        addressNumber: clientPlace?.number || "",
        phone: clientData?.whatsapp || clientData?.phone || "",
      },
      installments: "1",
    },
    mode: "onTouched",
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const [awaitingVisible, setAwaitingVisible] = useState<boolean>(false);
  const [approvedVisible, setApprovedVisible] = useState<boolean>(false);
  const [successTitle, setSuccessTitle] = useState<string>(
    "Pagamento aprovado!",
  );
  const [successMessage, setSuccessMessage] = useState<string>(
    "Seu pagamento foi confirmado com sucesso.",
  );
  const [errorVisible, setErrorVisible] = useState<boolean>(false);
  const [isEditingAddress, setIsEditingAddress] = useState<boolean>(false);
  const [userIp, setUserIp] = useState<string>("127.0.0.1");
  const [viewMode, setViewMode] = useState<"saved" | "new">(
    savedCards.length > 0 ? "saved" : "new",
  );
  const [selectedCardToken, setSelectedCardToken] = useState<string | null>(
    null,
  );

  const isPaid = isSalePaid(sale);

  const installmentCount = parseInt(methods.watch("installments") || "1");

  const paymentValues = sale
    ? calculatePaymentValues({
        sale,
        billingType: MethodPaymentEnum.CREDIT_CARD,
        installmentCount,
      })
    : null;

  useEffect(() => {
    if (isPaid) {
      setApprovedVisible(true);
    }
  }, [isPaid]);

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => setUserIp(data.ip))
      .catch((err) => console.error("Error fetching IP:", err));
  }, []);

  const onFormSubmit = (data: CreditCardFormDTO, token?: string) => {
    if (!sale) return;

    setApprovedVisible(false);
    setErrorVisible(false);
    setAwaitingVisible(true);

    const customer =
      clientData?.customerId ||
      clientData?._id ||
      (typeof sale.clientId === "string" ? sale.clientId : "");

    const installmentCount = parseInt(data.installments);

    const calc = calculatePaymentValues({
      sale,
      billingType: MethodPaymentEnum.CREDIT_CARD,
      installmentCount,
    });

    const payload: CreateCreditCardPaymentAsaasDTO = {
      saleId: sale.id,
      customer,
      billingType: "CREDIT_CARD",
      remoteIp: userIp,
      installmentCount,
      totalValue: calc.finalValue, // Send final value (with interest if applicable)
      value: calc.finalValue,
    };

    if (token) {
      payload.creditCardToken = token;
    } else {
      payload.creditCard = {
        ...data.creditCard,
        number: data.creditCard.number.replace(/\s/g, ""),
        expiryYear: `20${data.creditCard.expiryYear}`,
      };
      payload.creditCardHolderInfo = {
        ...data.creditCardHolderInfo,
        cpfCnpj: data.creditCardHolderInfo.cpfCnpj.replace(/\D/g, ""),
        phone: data.creditCardHolderInfo.phone.replace(/\D/g, ""),
        postalCode: data.creditCardHolderInfo.postalCode.replace(/\D/g, ""),
      };
    }

    mutateCreatePayment.mutate(payload, {
      onSuccess: (data) => {
        setAwaitingVisible(false);

        if (
          "status" in data &&
          (data.status === "PENDING" ||
            data.status === "AWAITING_RISK_ANALYSIS")
        ) {
          setSuccessTitle("Pagamento em análise");
          setSuccessMessage(
            "Seu pagamento está sendo processado e em breve será confirmado.",
          );
        } else {
          setSuccessTitle("Pagamento aprovado!");
          setSuccessMessage("Seu pagamento foi confirmado com sucesso.");
        }

        setApprovedVisible(true);
      },
      onError: (err: any) => {
        if (
          err.code === "PaymentValueMismatch" ||
          err.message?.includes("desatualizados")
        ) {
          window.location.reload();
          return;
        }
        console.error("Payment Error:", err.message || "Unknown error");
        setAwaitingVisible(false);
        setErrorVisible(true);
      },
    });
  };

  const handlePayClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (viewMode === "saved") {
      if (!selectedCardToken) {
        return;
      }
      methods.trigger("installments").then((isValid) => {
        if (isValid) {
          onFormSubmit(methods.getValues(), selectedCardToken);
        }
      });
    } else {
      handleSubmit((data) => onFormSubmit(data))(e);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center">
      <div className="w-full max-w-[800px] flex flex-col bg-white shadow-sm min-h-screen relative">
        <PaymentInnerHeader
          title="Cartão de Crédito"
          onBack={() => navigate(-1)}
        />

        <main className="flex-1 px-4 py-6 w-full flex flex-col gap-6">
          <SalesContractArea />

          <div className="bg-[#FCFCFC] rounded-[18px] p-4 border border-[#EDF2F6]">
            <h2 className="text-lg font-bold text-clarotexto-201 mb-4">
              Resumo do Pedido
            </h2>
            <ProductInfo
              title={sale ? getProductName(sale.productId) : "Carregando..."}
              subtitle={
                installmentCount === 1 ? "Preço à vista" : "Preço parcelado"
              }
              price={
                paymentValues ? (
                  <div className="flex flex-col items-end">
                    {paymentValues.discountApplied && (
                      <span className="text-sm text-gray-400 line-through">
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(paymentValues.originalValue)}
                      </span>
                    )}
                    <span className="text-xl font-bold text-claroprimaria-01">
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(paymentValues.finalValue)}
                    </span>
                    {paymentValues.discountApplied && (
                      <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full mt-1">
                        {paymentValues.discountApplied}%{" "}
                        {sale?.managerDiscount
                          ? "de desconto aplicado"
                          : installmentCount === 1
                            ? "de desconto à vista"
                            : "de desconto parcelado"}
                      </span>
                    )}
                    {installmentCount > 1 && (
                      <span className="text-xs text-gray-500">
                        {installmentCount}x de{" "}
                        {(
                          paymentValues.finalValue / installmentCount
                        ).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    )}
                  </div>
                ) : (
                  "..."
                )
              }
            />
          </div>

          {isPaid ? (
            <div className="bg-[#FCFCFC] rounded-[18px] p-6 border border-[#EDF2F6] flex flex-col items-center gap-4 text-center">
              <div className="size-16 rounded-full bg-green-50 flex items-center justify-center mb-2">
                <Check className="size-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-clarotexto-201">
                Pagamento confirmado!
              </h2>
              <p className="text-gray-500 max-w-sm">
                O pagamento deste pedido já foi realizado com sucesso. Você não
                precisa fazer mais nada.
              </p>
            </div>
          ) : (
            <FormProvider {...methods}>
              <form onSubmit={handlePayClick} className="flex flex-col gap-6">
                {viewMode === "saved" && (
                  <SavedCardsList
                    cards={savedCards}
                    selectedToken={selectedCardToken}
                    onSelect={setSelectedCardToken}
                    onAddNew={() => setViewMode("new")}
                    loading={mutateCreatePayment.isPending}
                  />
                )}

                {viewMode === "new" && (
                  <>
                    <BillingAddressSection
                      isEditing={isEditingAddress}
                      setIsEditing={setIsEditingAddress}
                    />

                    <CreditCardDataSection />

                    {savedCards.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setViewMode("saved")}
                        className="text-claroprimaria-01 font-semibold text-sm hover:underline self-start"
                      >
                        Voltar para cartões salvos
                      </button>
                    )}
                  </>
                )}

                {(viewMode === "new" ||
                  (viewMode === "saved" && selectedCardToken)) && (
                  <div className="bg-[#FCFCFC] rounded-[18px] p-5 border border-[#EDF2F6] flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-gray-500">
                        Parcelamento
                      </label>
                      <div className="relative border rounded-xl h-[48px] bg-clarofundo-01 transition-colors focus-within:bg-white focus-within:border-claroprimaria-01 border-transparent">
                        <select
                          {...methods.register("installments")}
                          className="w-full h-full px-4 bg-transparent outline-none text-clarotextos-01 appearance-none"
                        >
                          {installmentsOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                          <ChevronDown className="size-4 text-clarotextos-01" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3 mt-2">
                  <button
                    type="submit"
                    disabled={
                      mutateCreatePayment.isPending ||
                      (viewMode === "saved" && !selectedCardToken)
                    }
                    className="w-full bg-claroprimaria-01 hover:bg-[#1565af] active:bg-[#125391] disabled:opacity-50 disabled:cursor-not-allowed text-white h-[52px] rounded-xl font-semibold text-base transition-all shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
                  >
                    {mutateCreatePayment.isPending ? (
                      <>
                        <div className="size-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <Check className="size-5" />
                        Pagar agora
                      </>
                    )}
                  </button>
                </div>
              </form>
            </FormProvider>
          )}
        </main>

        <AwaitingApprovalModal
          isOpen={awaitingVisible}
          onClose={() => setAwaitingVisible(false)}
        />

        <PaymentErrorModal
          isOpen={errorVisible}
          onClose={() => setErrorVisible(false)}
          onRetry={() => {
            setErrorVisible(false);
          }}
        />

        <PaymentSuccessModal
          isOpen={approvedVisible}
          onClose={() => setApprovedVisible(false)}
          onFinalize={() => {
            setApprovedVisible(false);
            window.history.pushState(null, "", window.location.href);
          }}
          title={successTitle}
          message={successMessage}
        />
      </div>
    </div>
  );
}
